import { FunctionComponent, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Link, Navigate, useNavigate } from "react-router-dom";

import AppLogo from "@/assets/images/signalistlogo.png";

import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { InputField } from "@/components/InputField/InputField";

import { Button } from "@/components/Button/Button";

import { ButtonSize } from "@/components/Button/types";

import { Checkbox } from "@/components/Checkbox/Checkbox";

import { ROUTE_CONFIG } from "@/routes/config";

import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import { useIsFeatureActiveUnauthenticated } from "@/utils/useIsFeatureActiveUnauthenticated";

import { useRegisterAccountMutation } from "../../redux/auth.api";
import { RegisterType, RegisterSchema } from "./schema/register";
import { RegisterBackground } from "./components/RegisterBackground";
import { OnboardingStepDots } from "./components/OnboardingStepDots";

export const Register: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`register.${key}`);
	const [registerAccount, { isLoading, error }] = useRegisterAccountMutation();

	const navigate = useNavigate();

	const SELF_REGISTER = useIsFeatureActiveUnauthenticated(FeaturesCodes.SELF_REGISTER);

	const initialValues: RegisterType = {
		email: "",
		password: "",
		termsAndConditions: false,
	};

	const {
		handleSubmit,
		getValues,
		setValue,
		formState: { errors },
	} = useForm<RegisterType>({
		defaultValues: initialValues,
		resolver: zodResolver(RegisterSchema),
	});

	const [currentFormState, setCurrentFormState] = useState<Partial<RegisterType>>(initialValues);

	const handleSaveFormState = (
		value: RegisterType[keyof RegisterType],
		name: keyof RegisterType
	) => {
		setCurrentFormState({
			...currentFormState,
			[name]: value,
		});

		setValue(name, value, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	const formErrors = Object.values(errors).map((error) => error.message) as ERROR_TYPE[];

	const onSubmit = async ({ email, password }: RegisterType) => {
		try {
			await registerAccount({ email, password }).unwrap();

			// The register response authenticates the user directly — continue
			// straight into the Profile & Company onboarding step.
			navigate(ROUTE_CONFIG.ONBOARDING);
		} catch (err) {
			console.error(err);
		}
	};

	if (SELF_REGISTER === false) {
		return <Navigate to={ROUTE_CONFIG.LOGIN} />;
	}

	return (
		<div className="relative flex min-h-full w-full items-center justify-center p-4">
			<RegisterBackground />

			<div className="relative z-10 flex max-h-[calc(100vh-60px)] w-full max-w-[760px] flex-col rounded-2xl bg-white shadow-[0_20px_60px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.06)]">
				<div className="flex-1 overflow-y-auto px-6 py-9 md:px-12">
					<div className="mx-auto w-full max-w-[560px]">
						<div className="mb-6 flex flex-col items-center justify-center">
							<img alt="Signalist" className="mb-5 h-8 w-auto" src={AppLogo} />
							<div className="mb-2 text-md font-bold">{ts("createYourAccount")}</div>
							<div className="text-center text-ssm font-bold text-text-meta">
								{ts("yourFirst")}
								<span className="bg-gradient-to-r from-blue-action to-gradient-brand-to bg-clip-text text-transparent">
									{ts("freePersonalLeadLists")}
								</span>{" "}
								{ts("areJust")} {ts("aFewClicksAway")} {ts("onUs")}
							</div>
						</div>

						<form onSubmit={handleSubmit(onSubmit)}>
							<div className="flex flex-col">
								<div className="mb-5">
									<InputField
										error={!!errors.email?.message}
										handleChange={(event) => handleSaveFormState(event.target.value, "email")}
										label={ts("yourBusinessEmailAddress")}
										name="email"
										placeholder={ts("yourBusinessEmailAddressPlaceholder")}
										showError={!!errors.email?.message}
										type="email"
										value={currentFormState.email || ""}
									/>
								</div>
								<div className="mb-5">
									<InputField
										error={!!errors.password?.message}
										handleChange={(event) => handleSaveFormState(event.target.value, "password")}
										label={ts("password")}
										name="password"
										placeholder={ts("passwordPlaceholder")}
										showError={!!errors.password?.message}
										type="password"
										value={currentFormState.password || ""}
									/>
								</div>
								<div className="mb-4 flex w-full items-center">
									<Checkbox
										error={!!errors.termsAndConditions?.message}
										isChecked={getValues("termsAndConditions")}
										showError={!!errors.termsAndConditions?.message}
										onChange={() =>
											handleSaveFormState(!getValues("termsAndConditions"), "termsAndConditions")
										}
									>
										<div className="w-full">
											<Trans
												components={{
													link1: (
														<Link
															className="underline text-blue-action"
															color="primary"
															rel="noreferrer"
															target="_blank"
															to="https://www.signalist.io/terms"
														/>
													),
													link2: (
														<Link
															className="underline text-blue-action"
															color="primary"
															rel="noreferrer"
															target="_blank"
															to="/privacy-policy"
														/>
													),
												}}
												i18nKey="basics.termsAndConditions"
											/>
										</div>
									</Checkbox>
								</div>
								<Button
									className="w-full"
									isLoading={isLoading}
									size={ButtonSize.L}
									title={ts("signupForFree")}
									type="submit"
								/>
								<div className="mt-6 text-center text-xs font-normal text-text-meta">
									{ts("alreadyHaveAnAccount")}
									<Link className="font-bold underline" to="/login">
										{ts("login")}
									</Link>
								</div>
							</div>
						</form>
						<div className="mt-4">
							{getAllErrors(error, formErrors).length
								? renderErrorMessages(getAllErrors(error, formErrors))
								: null}
						</div>
					</div>
				</div>

				<div className="flex-shrink-0 border-t border-gray-200 py-4">
					<OnboardingStepDots currentStep={1} />
				</div>
			</div>
		</div>
	);
};
